import React, { useEffect, useMemo, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Programs tab (#289 phase 1): the separate-fee products sold through
// one-time Stripe Checkout — league registration, tourney-night entry.
// Each program is two Stripe Price ids (guest + optional member rate)
// behind a name; the dashboard "Register & pay" card shows every
// ACTIVE program to signed-in users at their tier price. Payments are
// recorded on the buyer's socialUsers row by the Stripe webhook and
// listed here, so captains and David can see who has paid without
// opening Stripe. Config lives in the socialConfig table (id=programs).

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const PROGRAMS_QUERY = `query ProgramsAdmin {
  programs(includeInactive: true) {
    id name description active nightEntry memberPriceId guestPriceId
    memberPriceCents guestPriceCents currency updatedAt updatedBy
  }
  programPayments(limit: 500) {
    userId name email programId programName amountCents currency tier eventId paidAt sessionId
  }
}`;

const SAVE_PROGRAM = `mutation SaveProgram($input: ProgramInput!) {
  saveProgram(input: $input) { id active }
}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmtPrice = (cents, currency) => {
  if (cents == null) return "—";
  const amt = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  return `$${amt}${currency && currency !== "usd" ? " " + currency.toUpperCase() : ""}`;
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 9px",
  border: "1px solid #d7dbe4",
  borderRadius: 6,
  fontSize: 14,
};
const label = { fontSize: 12, color: "#6a7385", fontWeight: 600, marginBottom: 3 };

const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
});

const Stat = ({ label: l, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 140px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{l}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const EMPTY_FORM = {
  id: "",
  name: "",
  description: "",
  guestPriceId: "",
  memberPriceId: "",
  active: true,
  nightEntry: false,
};

const ProgramsTab = () => {
  const [programs, setPrograms] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(PROGRAMS_QUERY);
      setPrograms(out?.programs || []);
      setPayments(out?.programPayments || []);
    } catch (err) {
      setError(err.message || "Couldn't load programs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      id: p.id,
      name: p.name || "",
      description: p.description || "",
      guestPriceId: p.guestPriceId || "",
      memberPriceId: p.memberPriceId || "",
      active: p.active,
      nightEntry: !!p.nightEntry,
    });
    setNotice("");
    setError("");
  };

  const resetForm = () => {
    setEditingId("");
    setForm(EMPTY_FORM);
  };

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await executeMutation(SAVE_PROGRAM, {
        input: {
          id: form.id.trim().toLowerCase(),
          name: form.name.trim(),
          description: form.description.trim(),
          guestPriceId: form.guestPriceId.trim(),
          memberPriceId: form.memberPriceId.trim() || null,
          active: !!form.active,
          nightEntry: !!form.nightEntry,
        },
      });
      setNotice(`Saved "${form.name.trim()}".`);
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || "Couldn't save the program.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    setBusyId(p.id);
    setError("");
    try {
      await executeMutation(SAVE_PROGRAM, {
        input: {
          id: p.id,
          name: p.name,
          description: p.description || "",
          guestPriceId: p.guestPriceId,
          memberPriceId: p.memberPriceId || null,
          active: !p.active,
          nightEntry: !!p.nightEntry,
        },
      });
      await load();
    } catch (err) {
      setError(err.message || "Couldn't update the program.");
    } finally {
      setBusyId("");
    }
  };

  const activeCount = programs.filter((p) => p.active).length;
  const totals = useMemo(() => {
    const byProgram = new Map();
    let cents = 0;
    for (const pay of payments) {
      cents += pay.amountCents || 0;
      const k = pay.programId || "?";
      byProgram.set(k, (byProgram.get(k) || 0) + 1);
    }
    return { cents, byProgram };
  }, [payments]);

  const shownPayments = useMemo(
    () =>
      filter === "all" ? payments : payments.filter((p) => p.programId === filter),
    [payments, filter]
  );

  const emails = useMemo(
    () => [...new Set(shownPayments.map((p) => p.email).filter(Boolean))],
    [shownPayments]
  );

  const copyEmails = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      setNotice(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}.`);
    } catch (err) {
      setNotice("Couldn't copy — select the list instead.");
    }
    setTimeout(() => setNotice(""), 2500);
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="receipt">🧾</span> Programs
      </h2>
      <p style={muted}>
        One-time fees sold through Stripe Checkout — league registration,
        tourney-night entry, anything that isn't the monthly membership or
        the Season Pass. Create the Price(s) in the Stripe Dashboard (one-time,
        not recurring), paste the ids here, and every <strong>active</strong>{" "}
        program shows on the dashboard's "Register &amp; pay" card at the
        viewer's rate: member standing (subscribers, Season Pass holders,
        approved members, captains) gets the member price, everyone else the
        guest price. Paying never changes membership or approval.
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          style={{
            background: "#eef7ee",
            border: "1px solid #b9dcb5",
            color: "#1f4f1d",
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {notice}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Stat label="On sale" value={activeCount} sub={`${programs.length} configured`} />
        <Stat label="Payments" value={payments.length} />
        <Stat label="Collected" value={fmtPrice(totals.cents, "usd")} sub="all programs, all time" />
      </div>

      {/* Programs list */}
      <div style={{ ...card, overflowX: "auto", padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Program</th>
              <th style={th}>Guest</th>
              <th style={th}>Member</th>
              <th style={th}>Paid</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && !programs.length && (
              <tr>
                <td style={{ ...td, ...muted }} colSpan={6}>Loading…</td>
              </tr>
            )}
            {!loading && programs.length === 0 && (
              <tr>
                <td style={{ ...td, ...muted }} colSpan={6}>
                  No programs yet — add the first one below.
                </td>
              </tr>
            )}
            {programs.map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid #f0f2f6" }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div style={{ ...muted, fontSize: 12 }}>
                    <code>{p.id}</code>
                    {p.description ? ` · ${p.description}` : ""}
                  </div>
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }} title={p.guestPriceId || ""}>
                  {fmtPrice(p.guestPriceCents, p.currency)}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }} title={p.memberPriceId || "same as guest"}>
                  {p.memberPriceId ? fmtPrice(p.memberPriceCents, p.currency) : <span style={muted}>same</span>}
                </td>
                <td style={td}>{totals.byProgram.get(p.id) || 0}</td>
                <td style={td}>
                  <span
                    style={{
                      background: p.active ? "#e4f5e8" : "#f0f2f6",
                      color: p.active ? "#1f7a3a" : "#6a7385",
                      borderRadius: 999,
                      padding: "2px 9px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {p.active ? "on sale" : "off"}
                  </span>
                  {p.nightEntry && (
                    <span
                      title="Season nights with door fees offer this program as the online entry; payments tagged with a night land on the buyer's RSVP row."
                      style={{
                        marginLeft: 6,
                        background: "#e6f4f8",
                        color: "#0e5f78",
                        borderRadius: 999,
                        padding: "2px 9px",
                        fontSize: 12,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      night entry
                    </span>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    onClick={() => startEdit(p)}
                    style={{ ...pill(editingId === p.id), padding: "3px 10px", fontSize: 12, marginRight: 6 }}
                  >
                    edit
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(p)}
                    disabled={busyId === p.id}
                    style={{ ...pill(false), padding: "3px 10px", fontSize: 12 }}
                  >
                    {p.active ? "take off sale" : "put on sale"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / edit form */}
      <form onSubmit={save} style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          {editingId ? `Edit "${editingId}"` : "Add a program"}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
          }}
        >
          <div>
            <div style={label}>Id (stable key, lowercase-dashes)</div>
            <input
              style={input}
              value={form.id}
              disabled={!!editingId}
              placeholder="tourney-entry"
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          </div>
          <div>
            <div style={label}>Name (what members see)</div>
            <input
              style={input}
              value={form.name}
              placeholder="Tourney night entry"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <div style={label}>Guest price id (Stripe, one-time) — required</div>
            <input
              style={input}
              value={form.guestPriceId}
              placeholder="price_…"
              onChange={(e) => setForm({ ...form, guestPriceId: e.target.value })}
            />
          </div>
          <div>
            <div style={label}>Member price id (optional; empty = guest price)</div>
            <input
              style={input}
              value={form.memberPriceId}
              placeholder="price_…"
              onChange={(e) => setForm({ ...form, memberPriceId: e.target.value })}
            />
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={label}>Description (one line under the name)</div>
          <input
            style={input}
            value={form.description}
            placeholder="Thursday tourney night — pays your entry, prize pool included"
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <label style={{ fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            On sale (shows on the dashboard)
          </label>
          <label
            style={{ fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}
            title="Flag ONE program. Season nights with door fees then show members a Pay-online button on the event's board; a payment made from there is stamped onto their RSVP as an online entry, so the captain's console and the pot count it."
          >
            <input
              type="checkbox"
              checked={!!form.nightEntry}
              onChange={(e) => setForm({ ...form, nightEntry: e.target.checked })}
            />
            Online entry for season nights
          </label>
          <span style={{ flex: 1 }} />
          {editingId && (
            <button type="button" onClick={resetForm} style={pill(false)}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            style={{ ...pill(true), opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Checking with Stripe…" : editingId ? "Save changes" : "Add program"}
          </button>
        </div>
        <div style={{ ...muted, marginTop: 8 }}>
          Saving verifies each price with Stripe (exists, one-time, not
          archived) before anything is written.
        </div>
      </form>

      {/* Payments */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <span style={{ fontWeight: 700 }}>Payments</span>
        <button type="button" style={pill(filter === "all")} onClick={() => setFilter("all")}>
          All
        </button>
        {programs.map((p) => (
          <button
            key={p.id}
            type="button"
            style={pill(filter === p.id)}
            onClick={() => setFilter(p.id)}
          >
            {p.name}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={copyEmails}
          disabled={!emails.length}
          style={{ ...pill(false), opacity: emails.length ? 1 : 0.5 }}
        >
          Copy {emails.length} email{emails.length === 1 ? "" : "s"}
        </button>
        <button type="button" onClick={load} style={pill(false)}>
          Refresh
        </button>
      </div>

      {!loading && shownPayments.length === 0 && (
        <p style={muted}>
          {payments.length === 0
            ? "No program payments yet. They appear here as soon as the Stripe webhook records one."
            : "No payments for this program yet."}
        </p>
      )}

      {shownPayments.length > 0 && (
        <div style={{ ...card, overflowX: "auto", padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>When</th>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Program</th>
                <th style={th}>Paid</th>
                <th style={th}>Rate</th>
                <th style={th}>Event</th>
              </tr>
            </thead>
            <tbody>
              {shownPayments.map((pay) => (
                <tr
                  key={pay.sessionId || `${pay.userId}-${pay.paidAt}`}
                  style={{ borderBottom: "1px solid #f0f2f6" }}
                >
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(pay.paidAt)}</td>
                  <td style={td}>{pay.name || <span style={muted}>(no name)</span>}</td>
                  <td style={{ ...td, wordBreak: "break-all" }}>{pay.email || ""}</td>
                  <td style={td}>{pay.programName || pay.programId}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtPrice(pay.amountCents, pay.currency)}</td>
                  <td style={td}>{pay.tier || ""}</td>
                  <td style={{ ...td, ...muted }}>{pay.eventId || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProgramsTab;
